/**
 * Structured editor for profiles/<applicant>/resume.yaml (or the root resume.yaml
 * fallback). Edits the high-traffic sections inline; advanced sections (roles[],
 * ground_truth_numbers) stay in a raw-YAML escape hatch so we don't lose them.
 */
import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import YAML from 'yaml';
import { api, ApiError } from '@/services/api';
import { Card } from '@/components/ui/Card';

interface ContactT {
  name?: string;
  phone?: string;
  email?: string;
  linkedin?: string;
  website?: string;
}
interface EducationT {
  school?: string;
  degree?: string;
  status?: string;
  graduated?: string;
  gpa?: string;
}
interface ProfileT {
  contact?: ContactT;
  education?: EducationT[];
  skills?: Record<string, string[]>;
  target_salary?: string;
  target_roles?: string[];
  target_cities?: string[];
  [k: string]: unknown;
}

interface CompletenessT {
  score: number;
  total_fields: number;
  filled_fields: number;
  missing: string[];
}

export function ProfileEditor({ applicantId }: { applicantId: string }) {
  const [profile, setProfile] = useState<ProfileT | null>(null);
  const [advancedYaml, setAdvancedYaml] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completeness, setCompleteness] = useState<CompletenessT | null>(null);

  async function loadCompleteness() {
    try {
      const r = await api.get<CompletenessT>(
        `/resumes/profile/completeness?applicant_id=${applicantId}`,
      );
      setCompleteness(r);
    } catch {
      setCompleteness(null);
    }
  }

  async function load() {
    setLoading(true);
    loadCompleteness();
    try {
      const r = await api.get<{ profile: ProfileT }>(`/resumes/profile?applicant_id=${applicantId}`);
      const p = r.profile ?? {};
      setProfile(p);
      // Advanced = everything except known structured keys
      const advanced = { ...p };
      delete advanced.contact;
      delete advanced.education;
      delete advanced.skills;
      delete advanced.target_salary;
      delete advanced.target_roles;
      delete advanced.target_cities;
      setAdvancedYaml(YAML.stringify(advanced));
    } catch (e) {
      if ((e as ApiError).status !== 404) {
        toast.error(e instanceof ApiError ? e.message : 'profile_load_failed');
      }
      setProfile({});
      setAdvancedYaml('');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId]);

  function patchContact(field: keyof ContactT, value: string) {
    setProfile((p) => ({ ...(p ?? {}), contact: { ...(p?.contact ?? {}), [field]: value } }));
  }

  function setEdu(idx: number, field: keyof EducationT, value: string) {
    setProfile((p) => {
      const edu = [...(p?.education ?? [])];
      edu[idx] = { ...(edu[idx] ?? {}), [field]: value };
      return { ...(p ?? {}), education: edu };
    });
  }
  function addEdu() {
    setProfile((p) => ({ ...(p ?? {}), education: [...(p?.education ?? []), {}] }));
  }
  function removeEdu(idx: number) {
    setProfile((p) => ({
      ...(p ?? {}),
      education: (p?.education ?? []).filter((_, i) => i !== idx),
    }));
  }

  function setSkillCat(cat: string, value: string) {
    setProfile((p) => ({
      ...(p ?? {}),
      skills: {
        ...(p?.skills ?? {}),
        [cat]: value
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      },
    }));
  }
  function addSkillCat() {
    const name = prompt('New skill category (snake_case):');
    if (!name) return;
    setProfile((p) => ({
      ...(p ?? {}),
      skills: { ...(p?.skills ?? {}), [name.toLowerCase().replace(/[^a-z0-9_]/g, '_')]: [] },
    }));
  }
  function removeSkillCat(cat: string) {
    setProfile((p) => {
      const s = { ...(p?.skills ?? {}) };
      delete s[cat];
      return { ...(p ?? {}), skills: s };
    });
  }

  async function save() {
    if (!profile) return;
    setSaving(true);
    try {
      let advanced: Record<string, unknown> = {};
      if (advancedYaml.trim()) {
        try {
          advanced = YAML.parse(advancedYaml) ?? {};
        } catch (e) {
          toast.error(`Advanced YAML invalid: ${(e as Error).message}`);
          setSaving(false);
          return;
        }
      }
      const merged: ProfileT = { ...advanced, ...profile };
      await api.post(`/resumes/profile?applicant_id=${applicantId}`, merged);
      toast.success('Profile saved');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'save_failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card title="Profile (resume.yaml)">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </Card>
    );
  }
  if (!profile) return null;

  const scoreColor =
    completeness == null
      ? 'bg-gray-300'
      : completeness.score >= 80
        ? 'bg-green-500'
        : completeness.score >= 50
          ? 'bg-amber-500'
          : 'bg-red-500';

  return (
    <Card
      title="Profile (resume.yaml)"
      action={
        <div className="flex items-center gap-3">
          {completeness && (
            <div className="hidden items-center gap-2 sm:flex">
              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className={`h-full ${scoreColor}`}
                  style={{ width: `${Math.min(100, completeness.score)}%` }}
                />
              </div>
              <span className="text-xs text-gray-500" title={`missing: ${completeness.missing.join(', ') || '—'}`}>
                {completeness.score}% complete
              </span>
            </div>
          )}
          <button onClick={save} disabled={saving} className="btn-primary text-xs">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
          </button>
        </div>
      }
    >
      {completeness && completeness.missing.length > 0 && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
          Missing: <span className="font-mono">{completeness.missing.join(', ')}</span>
        </div>
      )}
      <div className="space-y-6">
        {/* Contact */}
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Contact
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {(['name', 'phone', 'email', 'linkedin', 'website'] as const).map((k) => (
              <label key={k} className="block text-xs text-gray-700 dark:text-gray-300">
                {k}
                <input
                  value={profile.contact?.[k] ?? ''}
                  onChange={(e) => patchContact(k, e.target.value)}
                  className="input mt-0.5"
                />
              </label>
            ))}
          </div>
        </section>

        {/* Education */}
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Education</h4>
            <button onClick={addEdu} className="btn-secondary text-xs">
              <Plus className="h-3 w-3" />
              Add
            </button>
          </div>
          <ul className="space-y-3">
            {(profile.education ?? []).map((e, idx) => (
              <li key={idx} className="rounded-md bg-gray-50 p-3 dark:bg-gray-800">
                <div className="grid grid-cols-2 gap-2">
                  <input value={e.school ?? ''} onChange={(ev) => setEdu(idx, 'school', ev.target.value)} placeholder="School" className="input col-span-2" />
                  <input value={e.degree ?? ''} onChange={(ev) => setEdu(idx, 'degree', ev.target.value)} placeholder="Degree" className="input" />
                  <input value={e.graduated ?? ''} onChange={(ev) => setEdu(idx, 'graduated', ev.target.value)} placeholder="Graduated (e.g. May 2025)" className="input" />
                  <input value={e.gpa ?? ''} onChange={(ev) => setEdu(idx, 'gpa', ev.target.value)} placeholder="GPA" className="input" />
                  <input value={e.status ?? ''} onChange={(ev) => setEdu(idx, 'status', ev.target.value)} placeholder="Status / notes" className="input" />
                </div>
                <button onClick={() => removeEdu(idx)} className="mt-2 inline-flex items-center gap-1 text-xs text-red-700 hover:underline">
                  <Trash2 className="h-3 w-3" /> remove
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Targets — NEW fields, layered into resume.yaml */}
        <section>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Target preferences
          </h4>
          <div className="grid gap-2 md:grid-cols-3">
            <label className="block text-xs text-gray-700 dark:text-gray-300">
              Target salary
              <input
                value={profile.target_salary ?? ''}
                onChange={(e) => setProfile((p) => ({ ...(p ?? {}), target_salary: e.target.value }))}
                placeholder="$120k-$160k"
                className="input mt-0.5"
              />
            </label>
            <label className="block text-xs text-gray-700 dark:text-gray-300">
              Target roles (comma-separated)
              <input
                value={(profile.target_roles ?? []).join(', ')}
                onChange={(e) =>
                  setProfile((p) => ({
                    ...(p ?? {}),
                    target_roles: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  }))
                }
                placeholder="Product Manager, Creator PM"
                className="input mt-0.5"
              />
            </label>
            <label className="block text-xs text-gray-700 dark:text-gray-300">
              Target cities (comma-separated)
              <input
                value={(profile.target_cities ?? []).join(', ')}
                onChange={(e) =>
                  setProfile((p) => ({
                    ...(p ?? {}),
                    target_cities: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  }))
                }
                placeholder="NYC, SF, Atlanta, Remote"
                className="input mt-0.5"
              />
            </label>
          </div>
        </section>

        {/* Skills */}
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Skills (one per line)</h4>
            <button onClick={addSkillCat} className="btn-secondary text-xs">
              <Plus className="h-3 w-3" />
              Add category
            </button>
          </div>
          <div className="space-y-3">
            {Object.entries(profile.skills ?? {}).map(([cat, vals]) => (
              <div key={cat} className="rounded-md bg-gray-50 p-3 dark:bg-gray-800">
                <div className="mb-1 flex items-center justify-between">
                  <code className="text-xs text-gray-700 dark:text-gray-300">{cat}</code>
                  <button onClick={() => removeSkillCat(cat)} className="text-xs text-red-700 hover:underline">
                    <Trash2 className="inline h-3 w-3" /> remove
                  </button>
                </div>
                <textarea
                  value={(vals as string[]).join('\n')}
                  onChange={(e) => setSkillCat(cat, e.target.value)}
                  className="input min-h-[80px] font-mono text-xs"
                  spellCheck={false}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Advanced YAML for roles/ground_truth_numbers/etc */}
        <section>
          <button
            onClick={() => setShowAdvanced((s) => !s)}
            className="text-xs text-primary-700 hover:underline"
          >
            {showAdvanced ? 'Hide advanced YAML' : 'Show advanced YAML (roles, ground-truth metrics)'}
          </button>
          {showAdvanced && (
            <textarea
              value={advancedYaml}
              onChange={(e) => setAdvancedYaml(e.target.value)}
              className="input mt-2 min-h-[280px] font-mono text-xs"
              spellCheck={false}
              placeholder="# Sections not in the structured editor land here as YAML."
            />
          )}
        </section>
      </div>
    </Card>
  );
}
